import { defineMessages } from 'react-intl';

import Segment from 'helpers/segment';
import _range from 'lodash/range';

export const timeDropdownMessages = defineMessages({
  select: {
    id: 'smart-tools.release-extra-spots.select',
    defaultMessage: 'Select',
  },
  hours: {
    id: 'smart-tools.release-extra-spots.hours',
    defaultMessage: 'hours',
  },
  disable: {
    id: 'smart-tools.release-extra-spots.disable',
    defaultMessage: 'Disable',
  },
  venueDefault: {
    id: 'smart-tools.minimum-spots.venue-default',
    defaultMessage: 'Venue-level',
  },
});

export const SMARTSPOT_CATEGORY_TYPE = {
  ZERO_TO_THIRTY: '0-30%',
  THIRTY_TO_FOURTY: '30-40%',
  FOURTY_TO_FIFTY: '40-50%',
  FIFTY_TO_SIXTY: '50-60%',
  SIXTY_TO_SEVENTY: '60-70%',
  SEVENTY_TO_EIGHTY: '70-80%',
  EIGHTY_TO_NINETY: '80-90%',
  NINETY_TO_ONE_HUNDRED: '90-100%',
};

// Dropdown options for Release Extra Spots used on dashboard & onboarding
export const REMAINING_TIME_VALUES = [2, 3, 4, 5, 6, 12, 24, 36, 48, 72];

export function buildTimeDropdownOptions(intl, showSelectOption = true) {
  const baseOptions = REMAINING_TIME_VALUES.map(key => ({
    key,
    label: `${key} ${intl.formatMessage(timeDropdownMessages.hours)}`,
  }));

  const firstOptionLabel = showSelectOption
    ? intl.formatMessage(timeDropdownMessages.select)
    : intl.formatMessage(timeDropdownMessages.disable);

  return [
    {
      key: null,
      label: firstOptionLabel,
    },
    ...baseOptions,
  ];
}

// Dropdown options for venue-level Minimum Spots used on dashboard & onboarding
export function buildSpotsDropdownOptions(intl, showSelectOption = true) {
  const MINIMUM_SPOTS_RANGE_START = 1;
  const MINIMUM_SPOTS_RANGE_END = 51;

  const spotsOptions = _range(MINIMUM_SPOTS_RANGE_START, MINIMUM_SPOTS_RANGE_END).map(spots => ({
    key: spots,
    label: spots.toString(),
  }));

  const firstOptionLabel = showSelectOption
    ? intl.formatMessage(timeDropdownMessages.select)
    : intl.formatMessage(timeDropdownMessages.disable);

  return [
    {
      key: 0,
      label: firstOptionLabel,
    },
    ...spotsOptions,
  ];
}

// Dropdown options for class-level Minimum Spots
export function buildClassLevelSpotsDropdownOptions(intl) {
  const CLASS_LEVEL_SPOTS_RANGE_START = 0;
  const CLASS_LEVEL_SPOTS_RANGE_END = 51;

  const spotsOptions = _range(CLASS_LEVEL_SPOTS_RANGE_START, CLASS_LEVEL_SPOTS_RANGE_END).map(spots => ({
    key: spots,
    label: spots.toString(),
  }));

  return [
    {
      key: null,
      label: intl.formatMessage(timeDropdownMessages.venueDefault),
    },
    ...spotsOptions,
  ];
}

// Countries that use US/CA peak time definition
const US_CA_COUNTRIES = ['USA', 'CAN'];

// Helper function to get the appropriate peak time message based on country
export const getPeakTimeMessage = (countryCode = 'USA', messages) => {
  const isUSOrCA = US_CA_COUNTRIES.includes(countryCode);
  return isUSOrCA ? messages.peakTimeUSCA : messages.peakTimeNonUSCA;
};

// Helper functions below used to determine whether to show "Select" or "Disable" when building dropdown
// * - If venue is NOT set AND class level is NOT set is true show "Select"
// * - If venue IS set AND class level is explicitly 0 is true show "Select" (0 means already disabled)
// * - If venue IS set AND (class level is null OR class level > 0) is false show "Disable"
export const hasVenueLevelSetting = value => {
  return !!value;
};

export const isClassLevelExplicitlyZero = value => {
  return value === 0;
};

export const isClassLevelSet = value => {
  return value > 0;
};

export const shouldShowSelectOption = (hasVenueLevel, isClassLevelValueSet, isClassLevelZero) => {
  return (!hasVenueLevel && !isClassLevelValueSet) || (hasVenueLevel && isClassLevelZero);
};

export const getEffectiveMinimumSpotsValue = (classLevelValue, venueLevelDefault) => {
  if (classLevelValue !== null && classLevelValue !== undefined) {
    return classLevelValue;
  }
  if (venueLevelDefault !== null && venueLevelDefault !== undefined && venueLevelDefault !== 0) {
    return venueLevelDefault;
  }
  return 0;
};

export const trackReleaseExtraSpotsUpdated = (
  venueId,
  releaseExtraSpotsPeakTime,
  releaseExtraSpotsOffPeakTime,
  initialValues
) => {
  const releasePeakTimeChanged = releaseExtraSpotsPeakTime !== initialValues.releaseExtraSpotsPeakTime;
  const releaseOffPeakTimeChanged = releaseExtraSpotsOffPeakTime !== initialValues.releaseExtraSpotsOffPeakTime;

  if (releasePeakTimeChanged || releaseOffPeakTimeChanged) {
    const properties = {
      venue_id: venueId,
      peak: releasePeakTimeChanged ? releaseExtraSpotsPeakTime ?? 0 : null,
      off_peak: releaseOffPeakTimeChanged ? releaseExtraSpotsOffPeakTime ?? 0 : null,
    };
    Segment.event('Release Extra Spots Updated', properties);
  }
};

export const trackMinimumSpotsUpdated = (venueId, minimumSpotsPeakTime, minimumSpotsOffpeakTime, initialValues) => {
  const minimumSpotsPeakTimeChanged = minimumSpotsPeakTime !== initialValues.minimumSpotsPeakTime;
  const minimumSpotsOffpeakTimeChanged = minimumSpotsOffpeakTime !== initialValues.minimumSpotsOffpeakTime;

  if (minimumSpotsPeakTimeChanged || minimumSpotsOffpeakTimeChanged) {
    const properties = {
      venue_id: venueId,
      peak: minimumSpotsPeakTimeChanged ? minimumSpotsPeakTime : null,
      off_peak: minimumSpotsOffpeakTimeChanged ? minimumSpotsOffpeakTime : null,
    };
    Segment.event('Minimum Spots Updated', properties);
  }
};

export const trackClassLevelMinimumSpotsUpdated = (venueId, minimumSpotsData) => {
  Object.entries(minimumSpotsData).forEach(([classId, spotData]) => {
    const peakChanged = spotData.minimumSpotsPeakTime !== undefined;
    const offPeakChanged = spotData.minimumSpotsOffpeakTime !== undefined;

    if (peakChanged || offPeakChanged) {
      const properties = {
        venue_id: venueId,
        class_id: Number(classId),
        peak: peakChanged ? spotData.minimumSpotsPeakTime ?? 0 : null,
        off_peak: offPeakChanged ? spotData.minimumSpotsOffpeakTime ?? 0 : null,
      };
      Segment.event('Class Minimum Spots Updated', properties);
    }
  });
};
